/**
 * このクリニックの記事指示 — JMC writes the wording, the clinic asked for it.
 *
 * The queue and the editor sit on one screen because the operator's job is one
 * motion: read what the clinic asked for, compose the instruction, answer the
 * request. Splitting them across two screens is how "applied" gets recorded for
 * a change nobody made.
 *
 * The clinic's request text is displayed, never inserted. A one-click "apply
 * this text as the instruction" button would hand a tenant the prompt after all
 * - the whole reason this queue exists is that a person reads the request and
 * writes the instruction themselves.
 */
import { useRef, useState } from 'react';
import { ApiError, del, patch, post, put } from '../api.ts';
import { Card, Empty, Pill, Table, jaDate } from './ui.tsx';
import { Field } from './EditPanel.tsx';
import { Textarea } from './Textarea.tsx';
import { Modal, ModalForm } from './Modal.tsx';

export type StyledPromptKey = 'article.section' | 'article.body' | 'article.outline';

export interface ClinicPromptStyleAdmin {
  key: StyledPromptKey;
  userTemplate: string;
  styleNote: string;
  /** A passage in the voice this clinic wants. Shape only, never facts. */
  voiceSample: string;
  updatedAt: string | null;
  platformDefault: string;
}

/** One generation, the prompts written for it, and the one this clinic follows. */
export interface ClinicPromptUse {
  use: 'section' | 'body' | 'outline' | 'proposals' | 'article_image';
  chosenPromptId: string | null;
  options: Array<{ id: string; key: string; name: string; activeVersion: number | null }>;
}

export interface AdminPromptRequest {
  id: string;
  prompt_key: 'article.body' | 'article.outline' | null;
  body: string;
  status: 'OPEN' | 'APPLIED' | 'DECLINED';
  response: string;
  created_at: string;
  resolved_at: string | null;
  requested_by: string | null;
}

export interface ClinicPromptStyleData {
  clinicName: string;
  variables: string[];
  items: ClinicPromptStyleAdmin[];
  uses: ClinicPromptUse[];
  requests: AdminPromptRequest[];
}

const LABELS: Record<StyledPromptKey, string> = {
  'article.section': '節ごとの執筆（現在の本文生成）',
  'article.body': '記事全体の執筆（旧方式・予備）',
  'article.outline': '構成案の依頼文',
};

/** What each generation is, in the words an operator thinks in. */
const USE_LABELS: Record<ClinicPromptUse['use'], string> = {
  section: '節ごとの執筆',
  body: '記事全体の執筆',
  outline: '構成案',
  proposals: 'タイトル・見出し案',
  // Not the persona portrait, which is one prompt for everybody - this is the
  // scene direction for an article's own pictures. See migration 093.
  article_image: '記事の画像（カバー・本文中）',
};

const STATUS: Record<AdminPromptRequest['status'], { label: string; tone: 'ok' | 'warn' | 'muted' }> =
  {
    OPEN: { label: '未対応', tone: 'warn' },
    APPLIED: { label: '反映済み', tone: 'ok' },
    DECLINED: { label: '見送り', tone: 'muted' },
  };

function StyleEditor({
  clinicId,
  entry,
  variables,
  onSaved,
}: {
  clinicId: string;
  entry: ClinicPromptStyleAdmin;
  variables: string[];
  onSaved: () => void;
}) {
  const [styleNote, setStyleNote] = useState(entry.styleNote);
  const [sample, setSample] = useState(entry.voiceSample);
  const [template, setTemplate] = useState(entry.userTemplate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ system: string; user: string } | null>(null);
  const templateRef = useRef<HTMLTextAreaElement>(null);

  const dirty =
    styleNote !== entry.styleNote ||
    template !== entry.userTemplate ||
    sample !== entry.voiceSample;

  /** Insert at the caret, not at the end of a fifty-line instruction. */
  function insertVariable(name: string) {
    const area = templateRef.current;
    const token = `{{${name}}}`;
    if (!area) {
      setTemplate(`${template}${token}`);
      return;
    }
    const at = area.selectionStart;
    const end = area.selectionEnd;
    setTemplate(`${template.slice(0, at)}${token}${template.slice(end)}`);
    requestAnimationFrame(() => {
      area.focus();
      area.setSelectionRange(at + token.length, at + token.length);
    });
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await patch(`/admin/clinics/${clinicId}/prompt-styles/${entry.key}`, {
        styleNote,
        userTemplate: template,
        voiceSample: sample,
      });
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : '保存できませんでした',
      );
    } finally {
      setSaving(false);
    }
  }

  async function runPreview() {
    setError(null);
    try {
      setPreview(
        await post<{ system: string; user: string }>(
          `/admin/clinics/${clinicId}/prompt-styles/${entry.key}/preview`,
          { styleNote, userTemplate: template },
        ),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'プレビューできませんでした');
    }
  }

  return (
    <div style={{ marginBottom: 18 }}>
      <p className="form__section">
        {LABELS[entry.key]}
        {'　'}
        {entry.userTemplate.trim() || entry.voiceSample.trim() ? (
          <Pill tone="ok">専用</Pill>
        ) : (
          <Pill tone="muted">標準を使用</Pill>
        )}
      </p>

      <Field
        label="文体メモ（クリニックにも表示されます）"
        htmlFor={`${entry.key}-note`}
        span="full"
        hint="例：一人称は「当院」。費用は早めに提示。断定を避け、初診の方向けに補足する。"
      >
        <Textarea
          id={`${entry.key}-note`}
          minRows={3}
          maxLength={2000}
          value={styleNote}
          onChange={(e) => setStyleNote(e.target.value)}
        />
      </Field>

      {/*
        The single most effective control on this screen.

        A model calibrates register from examples, not from adjectives:
        「やわらかい文体で」 moves the output a little, one paragraph of the actual
        writing moves it a lot. It reaches the model as SHAPE - the rendering says
        so explicitly - and the approved-material block stays the only place a
        fact may come from, which is why pasting a real published column here is
        safe even though it is full of another branch's prices.
      */}
      <Field
        label="文体の見本（このクリニックの既存記事から1〜2段落）"
        htmlFor={`${entry.key}-sample`}
        span="full"
        hint="文の長さ・語尾・具体の入れ方を真似させます。見本に出てくる料金や回数は事実として使われません。"
      >
        <Textarea
          id={`${entry.key}-sample`}
          minRows={4}
          maxLength={3000}
          value={sample}
          placeholder="例：赤みは施術当日から翌日がもっとも強く、2〜3日で目立たなくなります。当日は洗顔とシャワーのみ…"
          onChange={(e) => setSample(e.target.value)}
        />
      </Field>

      {/*
        The standing instruction for this clinic's articles.
        
        Everything registered about the clinic already reaches the model as
        approved material; what this adds is the ability to say something about
        it - 「{{clinic_name}}（{{access}}）の記事として、{{persona}}に向けて」 -
        and have the values filled in per article. The chips below insert the
        names; the lint refuses one nothing supplies.
      */}
      <Field
        label="このクリニックへの指示（依頼文）"
        htmlFor={`${entry.key}-template`}
        span="full"
        hint="空欄なら標準の依頼文が使われます。{{ }} は記事ごとに登録済みの情報へ置き換わります。共通ルール（禁止表現・承認済み情報）はここでは変更できません。"
      >
        <Textarea
          id={`${entry.key}-template`}
          elementRef={templateRef}
          minRows={8}
          maxLength={8000}
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
        />
      </Field>

      <div className="chips" style={{ marginBottom: 8 }}>
        {variables.map((name) => (
          <button key={name} type="button" className="chip" onClick={() => insertVariable(name)}>
            {`{{${name}}}`}
          </button>
        ))}
      </div>

      <details>
        <summary className="muted small">標準の依頼文を見る</summary>
        <pre className="pre">{entry.platformDefault}</pre>
      </details>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="chips" style={{ marginTop: 10 }}>
        <button className="btn btn--sm" type="button" disabled={saving || !dirty} onClick={() => void save()}>
          {saving ? '保存中…' : '保存する（再認証が必要）'}
        </button>
        <button className="btn btn--sm btn--ghost" type="button" onClick={() => void runPreview()}>
          プレビュー
        </button>
        {entry.updatedAt && <span className="muted small">最終更新 {jaDate(entry.updatedAt)}</span>}
      </div>

      {preview && (
        <details open style={{ marginTop: 10 }}>
          <summary className="muted small">
            送信される依頼（サンプルのクリニックで組み立て）
          </summary>
          <pre className="pre">{preview.user}</pre>
        </details>
      )}
    </div>
  );
}

/**
 * Removing a request from the queue.
 *
 * Answering it is the normal end and keeps the record; this is for the rest -
 * a duplicate, a test row, something sent to the wrong clinic. Behind a
 * confirmation because the clinic's words are not recoverable afterwards: the
 * audit log keeps that a request was deleted, deliberately without its text.
 */
function RemoveRequest({
  entry,
  onRemoved,
}: {
  entry: AdminPromptRequest;
  onRemoved: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <>
      <button
        className="btn btn--sm btn--ghost"
        type="button"
        disabled={busy}
        onClick={() => setAsking(true)}
      >
        削除
      </button>
      <ModalForm
        open={asking}
        onClose={() => setAsking(false)}
        title="この依頼を削除しますか"
        caption="削除すると依頼の本文は元に戻せません。対応の記録として残す場合は「反映済み」か「見送り」を使ってください。"
        submitLabel="削除する"
        danger
        onSubmit={async () => {
          setBusy(true);
          try {
            await del(`/admin/prompt-requests/${entry.id}`);
            onRemoved();
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="field--full" style={{ margin: 0 }}>
          {jaDate(entry.created_at)}・{entry.requested_by ?? '依頼者不明'}
        </p>
      </ModalForm>
    </>
  );
}

function RequestRow({ entry, onResolved }: { entry: AdminPromptRequest; onResolved: () => void }) {
  const [response, setResponse] = useState(entry.response);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);

  async function resolve(status: 'APPLIED' | 'DECLINED') {
    setBusy(true);
    setError(null);
    try {
      await patch(`/admin/prompt-requests/${entry.id}`, { status, response });
      onResolved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新できませんでした');
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td className="muted small">
        {jaDate(entry.created_at)}
        <div>{entry.requested_by ?? '—'}</div>
      </td>
      <td>{entry.prompt_key === 'article.body' ? '本文' : entry.prompt_key ? '構成案' : '記事全体'}</td>
      <td className="requestcell">
        {/*
          The clinic's own words, shown as text. No "apply this" control: a
          request that could become the instruction with one click is the prompt
          injection this queue exists to prevent.

          Clamped to three lines with a way to open the whole thing: a request
          is free text up to two thousand characters, and one long one used to
          set the height of every row in the queue.
        */}
        <p className="requestcell__body">{entry.body}</p>
        <button
          className="btn btn--ghost btn--sm"
          type="button"
          onClick={() => setReading(true)}
        >
          全文を読む
        </button>

        <Modal
          open={reading}
          onClose={() => setReading(false)}
          title="クリニックからの依頼"
          caption={`${jaDate(entry.created_at)}・${entry.requested_by ?? '依頼者不明'}`}
        >
          {/*
            The clinic's text, verbatim and inert. `pre` because whitespace is
            part of what they wrote, and because a block that cannot be mistaken
            for the application's own words is the point.
          */}
          <pre className="pre">{entry.body}</pre>
          <div className="editpanel__actions">
            <button className="btn btn--ghost" type="button" onClick={() => setReading(false)}>
              閉じる
            </button>
          </div>
        </Modal>
      </td>
      <td>
        <Pill tone={STATUS[entry.status].tone}>{STATUS[entry.status].label}</Pill>
      </td>
      <td>
        {entry.status === 'OPEN' ? (
          <>
            <Textarea
              id={`resp-${entry.id}`}
              minRows={2}
              maxLength={2000}
              value={response}
              placeholder="クリニックへの回答"
              onChange={(e) => setResponse(e.target.value)}
            />
            {error && (
              <p className="alert" role="alert">
                {error}
              </p>
            )}
            <div className="chips" style={{ marginTop: 6 }}>
              <button
                className="btn btn--sm"
                type="button"
                disabled={busy}
                onClick={() => void resolve('APPLIED')}
              >
                反映済みにする
              </button>
              <button
                className="btn btn--sm btn--ghost"
                type="button"
                disabled={busy}
                onClick={() => void resolve('DECLINED')}
              >
                見送り
              </button>
              <RemoveRequest entry={entry} onRemoved={onResolved} />
            </div>
          </>
        ) : (
          <>
            <span className="muted">{entry.response || '—'}</span>
            <div className="chips" style={{ marginTop: 6 }}>
              <RemoveRequest entry={entry} onRemoved={onResolved} />
            </div>
          </>
        )}
      </td>
    </tr>
  );
}

/**
 * Which prompt this clinic follows, per generation.
 *
 * One instruction for every clinic on the platform was right for the RULES and
 * wrong for everything else: a clinic writing for surgeons needs a different
 * brief from one writing for first-time patients in their twenties, and no
 * amount of style notes bolted onto one instruction gets there.
 *
 * The row names a PROMPT, not a version, so the release discipline is untouched:
 * this clinic follows that prompt's active version, and a rollback still reaches
 * everybody at once. 「標準」 clears the row, which is the way back rather than a
 * second control.
 */
function UsePicker({
  clinicId,
  entry,
  onChanged,
}: {
  clinicId: string;
  entry: ClinicPromptUse;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function choose(promptId: string) {
    setBusy(true);
    setError(null);
    try {
      await put(`/admin/clinics/${clinicId}/prompt-choice/${entry.use}`, {
        promptId: promptId === '' ? null : promptId,
      });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '変更できませんでした');
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td>{USE_LABELS[entry.use]}</td>
      <td>
        {entry.options.length === 0 ? (
          <span className="muted small">この用途のプロンプトは登録されていません（標準を使用）</span>
        ) : (
          <select
            className="input"
            aria-label={`${USE_LABELS[entry.use]}に使うプロンプト`}
            disabled={busy}
            value={entry.chosenPromptId ?? ''}
            onChange={(e) => void choose(e.target.value)}
          >
            <option value="">標準（プラットフォーム既定）</option>
            {entry.options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
                {option.activeVersion === null ? '（未公開）' : `（v${option.activeVersion}）`}
              </option>
            ))}
          </select>
        )}
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
      </td>
      <td>
        {entry.chosenPromptId ? <Pill tone="ok">専用</Pill> : <Pill tone="muted">標準</Pill>}
      </td>
    </tr>
  );
}

export function ClinicPromptEditor({
  clinicId,
  data,
  onChanged,
}: {
  clinicId: string;
  data: ClinicPromptStyleData;
  onChanged: () => void;
}) {
  const open = data.requests.filter((entry) => entry.status === 'OPEN').length;
  /*
   * The row every article is written from, and the rest. See the comment where
   * they are rendered: three identical blocks of fields is a screen where nobody
   * can tell which one is in force.
   */
  const primary = data.items.find((entry) => entry.key === 'article.section');
  /*
   * Style notes saved against the other prompt keys before this screen stopped
   * offering them. Only the ones that actually carry text: an empty row is not
   * state anybody needs to be told about.
   */
  const carried = data.items.filter(
    (entry) =>
      entry.key !== 'article.section' &&
      (entry.styleNote.trim() !== '' ||
        entry.userTemplate.trim() !== '' ||
        entry.voiceSample.trim() !== ''),
  );
  const [clearing, setClearing] = useState(false);

  async function clearCarried() {
    setClearing(true);
    try {
      for (const entry of carried) {
        await patch(`/admin/clinics/${clinicId}/prompt-styles/${entry.key}`, {
          styleNote: '',
          userTemplate: '',
          voiceSample: '',
        });
      }
      onChanged();
    } finally {
      setClearing(false);
    }
  }

  return (
    <Card
      title="このクリニックの記事指示"
      caption="生成に使う依頼文はJMCが書きます。クリニックからの依頼はここで確認して反映します。"
      actions={open > 0 ? <Pill tone="warn">未対応 {open}件</Pill> : null}
    >
      {/*
        Which instruction runs, before the wording that sits under it: an
        operator deciding this reads top to bottom, and the choice changes what
        the style note below is even modifying.
      */}
      <p className="form__section">使用するプロンプト</p>
      <Table head={['生成', 'プロンプト', '状態']}>
        {data.uses.map((entry) => (
          <UsePicker key={entry.use} clinicId={clinicId} entry={entry} onChanged={onChanged} />
        ))}
      </Table>
      <p className="muted small">
        公開中のバージョンに従います。切り戻しはプロンプト側で行い、すべてのクリニックに同時に反映されます。
      </p>

      {/*
        One editor, not three.

        There is a wording row for every generation that writes prose, and
        stacking them produced a screen with the same four fields three times -
        an operator cannot tell from looking which one their articles are
        actually written from.
        
        Sections are that one: it is the path every article takes now. The other
        two matter rarely - the outline round, and the whole-article prompt kept
        as a fallback - so they are here, folded away, rather than competing with
        the row that does the work.
      */}
      <p className="form__section">このクリニックの文体と依頼文</p>
      {primary && (
        <StyleEditor
          clinicId={clinicId}
          entry={primary}
          variables={data.variables}
          onSaved={onChanged}
        />
      )}

      {/*
        The other generations' style notes are not edited here any more.

        There is a wording row per prompt key, so this screen offered the same
        four fields three times - once for sections, once for the outline round,
        once for the whole-article fallback. Two of those are the same request
        written twice: what a clinic wants said is a property of the clinic, not
        of which round of generation is running, and nobody could tell from
        looking which of the three their articles came out of. The row above is
        the one every article takes, and 使用するプロンプト is where a clinic gets
        a genuinely different instruction.

        Anything already saved on the other keys still applies, so it is named
        rather than hidden: invisible state that changes output is worse than a
        field nobody uses.
      */}
      {carried.length > 0 && (
        <p className="notice">
          <strong>他の生成に、以前保存された指示文が残っています。</strong>
          <br />
          {carried.map((entry) => LABELS[entry.key]).join('・')}
          にこのクリニック固有の指示が保存されており、その生成では今も使われます。
          文体はこの画面の上の1つにまとめました。不要であれば消去できます。
          <br />
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            style={{ marginTop: 8 }}
            disabled={clearing}
            onClick={() => void clearCarried()}
          >
            {clearing ? '消去中…' : '残っている指示文を消去する'}
          </button>
        </p>
      )}

      <p className="form__section">クリニックからの依頼</p>
      {data.requests.length === 0 ? (
        <Empty>依頼はありません。</Empty>
      ) : (
        <Table head={['依頼日・依頼者', '対象', '内容', '状況', '回答']}>
          {data.requests.map((entry) => (
            <RequestRow key={entry.id} entry={entry} onResolved={onChanged} />
          ))}
        </Table>
      )}
    </Card>
  );
}
